import { Category, CategoryCategory, emptyCategory } from "@greeter/core";
import { NotProvidedError } from "@greeter/xstate-util";
import {
  assign,
  emit,
  fromPromise,
  raise,
  setup,
  enqueueActions,
} from "xstate";
import { CategoryEditorApiRequirements } from "./CategoryEditor";
import { change, ChangeState, createChangeState } from "@greeter/changes";

export type CategoryChange =
  | { type: "setName"; data: string }
  | { type: "setCategoryCategory"; data: string | undefined };

function apply(c: Category, change: CategoryChange) {
  console.log("category", c, change);
  switch (change.type) {
    case "setName":
      c.name = change.data;
      break;
    case "setCategoryCategory":
      c.categoryCategoryId = change.data;
      break;
  }
}

type CategoryEditorEvents =
  | { type: "change"; data: CategoryChange }
  | { type: "idle" }
  | { type: "blockFetch" }
  | { type: "fetchCategory" }
  | { type: "fetchCategoryCategories" }
  | { type: "save" };

type CategoryEditorContext = {
  categoryId?: string;
  categoryCategories: Array<CategoryCategory>;

  changes: ChangeState<Category, CategoryChange>;
};

export function provideApiForActor(
  m: typeof categoryEditorMachine,
  api: CategoryEditorApiRequirements
) {
  return m.provide({
    actors: {
      saveCategory: fromPromise(async ({ input }) => {
        return await api.saveCategory(input.category);
      }),
      fetchCategory: fromPromise(async ({ input }) => {
        return await api.fetchCategory(input.categoryId);
      }),
      fetchCategoryCategories: fromPromise(async () => {
        return await api.fetchCategoryCategories();
      }),
    },
  });
}

export type CategoryEditorEmitted =
  | { type: "saving" }
  | { type: "saved" }
  | { type: "fetchedCategoryCategories" };

export const categoryEditorMachine = setup({
  types: {} as {
    events: CategoryEditorEvents;
    context: CategoryEditorContext;
    input: {
      categoryId?: string;
    };
  },
  actors: {
    fetchCategoryCategories: fromPromise(
      (): Promise<Array<CategoryCategory>> => {
        throw new NotProvidedError();
      }
    ),
    fetchCategory: fromPromise(
      (args: { input: { categoryId: string } }): Promise<Category> => {
        throw new NotProvidedError();
      }
    ),
    saveCategory: fromPromise(
      (args: { input: { category: Category } }): Promise<void> => {
        throw new NotProvidedError();
      }
    ),
  },
}).createMachine({
  context: ({ input }) => ({
    categoryId: input?.categoryId,
    categoryCategories: [],

    changes: createChangeState(emptyCategory()),
  }),

  type: "parallel",

  states: {
    api: {
      type: "parallel",
      states: {
        categoryCategories: {
          initial: "fetching",
          states: {
            idle: {
              on: {
                fetchCategoryCategories: "fetching",
              },
            },
            fetching: {
              invoke: {
                src: "fetchCategoryCategories",
                onDone: {
                  target: "idle",
                  actions: [
                    assign(({ event }) => {
                      return { categoryCategories: event.output };
                    }),
                    emit({ type: "fetchedCategoryCategories" }),
                  ],
                },
                onError: "idle",
              },
            },
          },
        },
        category: {
          initial: "init",
          states: {
            init: {
              entry: enqueueActions(({ context, enqueue }) => {
                if (!!context.categoryId) {
                  enqueue.raise({ type: "fetchCategory" });
                } else {
                  enqueue.raise({ type: "idle" });
                }
              }),
              on: {
                idle: "idle",
                fetchCategory: "fetching",
              },
            },
            idle: {
              on: {
                fetchCategory: {
                  target: "fetching",
                  guard({ context }) {
                    return !!context.categoryId;
                  },
                },
                save: "saving",
              },
            },
            fetching: {
              invoke: {
                src: "fetchCategory",
                input: ({ context }) => ({
                  categoryId: context.categoryId ?? "",
                }),
                onDone: {
                  target: "idle",
                  actions: [
                    assign(({ context, event }) => {
                      return {
                        changes: createChangeState(
                          event.output,
                          context.changes.changes
                        ),
                      };
                    }),
                  ],
                },
                onError: "idle",
              },
            },
            saving: {
              entry: emit({ type: "saving" }),
              invoke: {
                src: "saveCategory",
                input: ({ context }) => ({ category: context.changes.value }),
                onDone: { target: "idle", actions: [emit({ type: "saved" })] },
              },
            },
          },
        },
      },
    },
    ui: {
      on: {
        change: {
          actions: assign(({ context, event }) => {
            console.log(
              `CategoryEditorMachine: `,
              context.changes.value,
              event.data
            );
            return {
              changes: { ...change(context.changes, apply, event.data) },
            };
          }),
        },
      },
    },
  },
});
